import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { getTables, getTableColumns, getTableData, TableInfo, ColumnInfo } from '../services/api';

export default function SchemaBrowser() {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableColumns, setTableColumns] = useState<{ [key: string]: ColumnInfo[] }>({});
  const [tableData, setTableData] = useState<{ [key: string]: any }>({});
  const [loadingColumns, setLoadingColumns] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const tablesData = await getTables();
      setTables(tablesData);
    } catch (err: any) {
      setError('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const handleTableExpand = async (table: TableInfo) => {
    const tableKey = `${table.schema}.${table.name}`;
    
    if (tableColumns[tableKey]) return; // Already loaded

    try {
      setLoadingColumns({ ...loadingColumns, [tableKey]: true });
      const columns = await getTableColumns(table.schema, table.name);
      setTableColumns({ ...tableColumns, [tableKey]: columns });
    } catch (err) {
      console.error('Failed to fetch table columns:', err);
    } finally {
      setLoadingColumns({ ...loadingColumns, [tableKey]: false });
    }
  };

  const handlePreviewData = async (table: TableInfo) => {
    const tableKey = `${table.schema}.${table.name}`;
    
    try {
      const data = await getTableData(table.schema, table.name, 5);
      setTableData({ ...tableData, [tableKey]: data });
    } catch (err) {
      console.error('Failed to fetch table data:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Database Schema Browser
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        Browse tables, columns, and preview data in your database.
      </Typography>

      {tables.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No tables found in the database.
        </Alert>
      ) : (
        <Box sx={{ mt: 2 }}>
          {tables.map((table) => {
            const tableKey = `${table.schema}.${table.name}`;
            const columns = tableColumns[tableKey] || [];
            const data = tableData[tableKey];
            const isLoadingColumns = loadingColumns[tableKey];

            return (
              <Accordion 
                key={tableKey}
                onChange={(_, expanded) => expanded && handleTableExpand(table)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                    <Typography variant="h6">
                      {table.schema}.{table.name}
                    </Typography>
                    {table.row_count !== null && (
                      <Chip 
                        label={`${table.row_count} rows`} 
                        size="small" 
                        color="primary"
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewData(table);
                      }}
                      sx={{ ml: 'auto' }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {isLoadingColumns ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : columns.length > 0 ? (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Columns
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Column Name</TableCell>
                              <TableCell>Data Type</TableCell>
                              <TableCell>Nullable</TableCell>
                              <TableCell>Default</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {columns.map((column, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <strong>{column.column_name}</strong>
                                </TableCell>
                                <TableCell>{column.data_type}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={column.is_nullable === 'YES' ? 'Yes' : 'No'}
                                    size="small"
                                    color={column.is_nullable === 'YES' ? 'default' : 'error'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {column.column_default || <em>None</em>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {data && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            Sample Data (first 5 rows)
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {data.columns.map((column: string, index: number) => (
                                    <TableCell key={index}>
                                      <strong>{column}</strong>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {data.rows.map((row: any[], rowIndex: number) => (
                                  <TableRow key={rowIndex}>
                                    {row.map((cell: any, cellIndex: number) => (
                                      <TableCell key={cellIndex}>
                                        {cell !== null ? String(cell) : <em>NULL</em>}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      No column information available
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}